import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Brain, FlaskConical, Leaf, Sparkles, ExternalLink, ArrowRight, Clock, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_META = {
  evidence_based: {
    icon: FlaskConical,
    accent: 'emerald',
    bgGlow: 'from-emerald-500/10 to-transparent',
    border: 'border-emerald-500/30',
    textColor: 'text-emerald-300',
    bg: 'bg-emerald-500/10',
  },
  applied_neuroscience: {
    icon: Brain,
    accent: 'cyan',
    bgGlow: 'from-cyan-500/10 to-transparent',
    border: 'border-cyan-500/30',
    textColor: 'text-cyan-300',
    bg: 'bg-cyan-500/10',
  },
  traditional: {
    icon: Leaf,
    accent: 'amber',
    bgGlow: 'from-amber-500/10 to-transparent',
    border: 'border-amber-500/30',
    textColor: 'text-amber-300',
    bg: 'bg-amber-500/10',
  },
  experimental: {
    icon: Sparkles,
    accent: 'fuchsia',
    bgGlow: 'from-fuchsia-500/10 to-transparent',
    border: 'border-fuchsia-500/30',
    textColor: 'text-fuchsia-300',
    bg: 'bg-fuchsia-500/10',
  },
};

const setMetaTag = (name, content, property = false) => {
  const attr = property ? 'property' : 'name';
  let tag = document.querySelector(`meta[${attr}="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const SciencePage = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    // SEO: set page-specific meta tags
    document.title = 'Science & Research — NeuroWave One | Every Protocol, Cited Honestly';
    const desc = 'Every brainwave-entrainment protocol in NeuroWave One, organized by evidence level. ' +
                 'Peer-reviewed research with DOI links, plus honestly-labeled traditional and experimental protocols. ' +
                 'No pseudoscience hidden behind buzzwords.';
    // Absolute URL so previews on external sites (Twitter/X, LinkedIn, Slack, Discord) load the image
    const ogImage = `${window.location.origin}/og-science.png`;
    setMetaTag('description', desc);
    setMetaTag('og:title', 'NeuroWave One — Science & Research', true);
    setMetaTag('og:description', desc, true);
    setMetaTag('og:type', 'website', true);
    setMetaTag('og:image', ogImage, true);
    setMetaTag('og:image:width', '1200', true);
    setMetaTag('og:image:height', '630', true);
    setMetaTag('og:url', `${window.location.origin}/science`, true);
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', 'NeuroWave One — Science & Research');
    setMetaTag('twitter:description', desc);
    setMetaTag('twitter:image', ogImage);

    fetch(`${API}/presets/catalog`)
      .then((r) => r.json())
      .then(setData)
      .catch((e) => setError(e.message));
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6">
        <p className="text-red-400 text-sm">Failed to load catalog: {error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-7 h-7 text-purple-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-[#0e0a1f] to-slate-950 text-white">
      {/* Header */}
      <header className="max-w-6xl mx-auto px-5 sm:px-8 pt-10 pb-6">
        <div className="flex items-center justify-between mb-16">
          <Link to="/" className="flex items-center gap-2 group" data-testid="science-home-link">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-600 to-cyan-500 flex items-center justify-center group-hover:scale-105 transition">
              <Brain className="w-4 h-4 text-white" />
            </div>
            <span className="text-white/80 group-hover:text-white font-semibold">NeuroWave One</span>
          </Link>
          <Link to="/app" data-testid="science-open-app-link">
            <Button className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white px-5 py-2.5 rounded-xl text-sm font-semibold">
              Open App <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </Link>
        </div>

        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/60 text-xs mb-5">
            <FlaskConical className="w-3.5 h-3.5 text-emerald-400" />
            Science & Research — {data.total} protocols
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-5" data-testid="science-h1">
            Every protocol,{' '}
            <span className="bg-gradient-to-r from-purple-300 via-cyan-300 to-purple-300 bg-clip-text text-transparent">
              cited honestly.
            </span>
          </h1>
          <p className="text-white/55 text-base sm:text-lg leading-relaxed mb-6">
            Other apps hide their &ldquo;secret sauce&rdquo; behind marketing. NeuroWave tells you exactly which
            protocols are peer-reviewed, which are established clinical practice, which are spiritual tradition,
            and which are experimental — so you can make an informed choice about what to listen to.
          </p>

          {/* Tier nav */}
          <nav className="flex flex-wrap gap-2 text-xs mt-4">
            {data.tiers.map((t) => {
              const meta = TIER_META[t.key];
              const Icon = meta?.icon || Brain;
              return (
                <a
                  key={t.key}
                  href={`#${t.key}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${meta?.border} ${meta?.bg} ${meta?.textColor} hover:brightness-125 transition`}
                  data-testid={`science-nav-${t.key}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {t.label} <span className="opacity-60">({t.count})</span>
                </a>
              );
            })}
          </nav>
        </div>
      </header>

      {/* Tier sections */}
      <main className="max-w-6xl mx-auto px-5 sm:px-8 pb-24">
        {data.tiers.map((tier) => {
          if (!tier.presets.length) return null;
          const meta = TIER_META[tier.key];
          const Icon = meta?.icon || Brain;
          return (
            <section
              key={tier.key}
              id={tier.key}
              className={`relative mt-20 scroll-mt-12`}
              data-testid={`science-section-${tier.key}`}
            >
              <div className={`absolute inset-0 bg-gradient-to-b ${meta?.bgGlow} pointer-events-none rounded-3xl -z-10`} />
              <div className="flex items-start gap-4 mb-8">
                <div className={`shrink-0 w-12 h-12 rounded-2xl ${meta?.bg} border ${meta?.border} flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${meta?.textColor}`} />
                </div>
                <div>
                  <h2 className={`text-2xl sm:text-3xl font-bold mb-2`} data-testid={`science-h2-${tier.key}`}>
                    {tier.label}
                    <span className="text-white/30 text-base font-medium ml-3">{tier.count} protocols</span>
                  </h2>
                  <p className="text-white/50 text-sm leading-relaxed max-w-2xl">{tier.blurb}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {tier.presets.map((p) => (
                  <article
                    key={p.name}
                    className="bg-white/[0.03] hover:bg-white/[0.05] border border-white/5 hover:border-white/10 rounded-2xl p-5 transition"
                    data-testid="science-preset-card"
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <h3 className="text-white text-base font-semibold leading-snug">{p.name}</h3>
                      {p.category === 'premium' && (
                        <span className="shrink-0 text-[10px] bg-purple-500/15 text-purple-300 border border-purple-500/30 rounded-full px-2 py-0.5">
                          Premium
                        </span>
                      )}
                    </div>
                    <p className="text-white/55 text-sm leading-relaxed mb-3">{p.description}</p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-white/40 mb-3">
                      <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {p.duration_minutes} min</span>
                      <span>{p.beat_type}</span>
                      <span>{p.base_frequency} Hz carrier</span>
                      {p.best_time && <span className="text-white/50">· Best: {p.best_time}</span>}
                    </div>
                    {p.research_citation && (
                      <div className="mt-3 pt-3 border-t border-white/5">
                        <p className="text-[11px] text-white/50 leading-relaxed mb-1.5">
                          <span className={`font-semibold ${meta?.textColor}`}>Research note. </span>
                          {p.research_citation}
                        </p>
                        {p.research_url && (
                          <a
                            href={p.research_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`inline-flex items-center gap-1 text-[11px] ${meta?.textColor} hover:brightness-125 underline-offset-2 hover:underline`}
                            data-testid="science-research-link"
                          >
                            View source <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    )}
                    {p.tags && p.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {p.tags.map((t) => (
                          <span key={t} className="text-[10px] bg-white/[0.04] text-white/40 px-2 py-0.5 rounded-full">
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </article>
                ))}
              </div>
            </section>
          );
        })}

        {/* Disclaimer */}
        <section className="mt-20 p-6 rounded-2xl bg-amber-500/[0.06] border border-amber-500/20" data-testid="science-disclaimer">
          <h3 className="text-amber-300 text-sm font-semibold mb-2">A note on safety & scope</h3>
          <p className="text-white/55 text-xs leading-relaxed">
            NeuroWave One is a wellness and meditation tool. It is <strong className="text-white/75">not a medical device</strong> and does not
            diagnose, treat, cure, or prevent any disease. If you have epilepsy, a pacemaker, are pregnant, or are taking
            medication that affects the nervous system, consult your clinician before use. Never use while driving or operating
            machinery. Sessions can produce drowsiness or altered awareness.
          </p>
        </section>

        {/* Final CTA */}
        <section className="mt-20 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4" data-testid="science-final-cta-h2">
            Ready to try a session?
          </h2>
          <p className="text-white/50 text-sm mb-6 max-w-xl mx-auto">
            10 evidence-based protocols are free forever. Premium unlocks the full catalog of {data.total} for a one-time $9.99.
          </p>
          <Link to="/app" data-testid="science-final-cta-link">
            <Button className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white px-7 py-6 rounded-xl font-semibold">
              Open NeuroWave One <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </section>
      </main>
    </div>
  );
};

export default SciencePage;
